import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Key, Check, Copy, Info, ChevronRight, Settings, Link as LinkIcon, AlertTriangle, AppWindow, User, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useServicePrincipal, useServicePrincipals } from '@/store/service-principal';
import { useApplications } from '@/store/application';
import { useUsers } from '@/store/user';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { TabBar } from '@/components/ui/TabBar';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import type { SpnSecret, SpnSecretCreated } from '@/types';

export const ManageServicePrincipalPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const { servicePrincipal, update, delete: deleteSpn, listSecrets, createSecret, deleteSecret } = useServicePrincipal(id);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const [activeTab, setActiveTab] = useState('details');

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Secrets state
    const [secrets, setSecrets] = useState<SpnSecret[]>([]);
    const [showCreateSecret, setShowCreateSecret] = useState(false);
    const [newSecretResponse, setNewSecretResponse] = useState<SpnSecretCreated | null>(null);
    const [showDeleteSecretConfirmKeyId, setShowDeleteSecretConfirmKeyId] = useState<string | null>(null);
    const [deleteSecretConfirmText, setDeleteSecretConfirmText] = useState('');

    const formatLocalDate = (date: Date) => {
        const tzoffset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - tzoffset).toISOString().slice(0, 16);
    };

    const [secretForm, setSecretForm] = useState({
        name: '',
        valid_from: formatLocalDate(new Date()),
        valid_to: formatLocalDate(new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())),
    });

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        redirect_uri: '',
    });

    const [linkData, setLinkData] = useState<{ type: 'application' | 'user' | 'none', id: string }>({
        type: 'none',
        id: ''
    });

    const { applications, list: listApps } = useApplications();
    const { users, list: listUsers } = useUsers();

    useEffect(() => {
        listApps();
        listUsers();
    }, [listApps, listUsers]);

    useEffect(() => {
        if (!id) return;
        useServicePrincipals.getState().get(id)
            .then((data) => {
                setFormData({
                    name: data.name,
                    description: data.description || '',
                    redirect_uri: data.redirect_uri || '',
                });
                
                if (data.application_id) {
                    setLinkData({ type: 'application', id: data.application_id });
                } else if (data.user_id) {
                    setLinkData({ type: 'user', id: data.user_id });
                } else {
                    setLinkData({ type: 'none', id: '' });
                }

                setBreadcrumbs([]);
            })
            .catch(() => {
                navigate('/m/service-principals');
            })
            .finally(() => setLoading(false));
    }, [id, navigate, setBreadcrumbs]);

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await update({
                name: formData.name,
                description: formData.description || undefined,
                redirect_uri: formData.redirect_uri || undefined,
            });
            toast.success('Service Principal updated');
        } catch (err) {
            console.error(err);
            toast.error('Failed to update Service Principal');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateLinks = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await update({
                application_id: linkData.type === 'application' ? linkData.id : null as any,
                user_id: linkData.type === 'user' ? linkData.id : null as any,
            });
            toast.success('Service Principal linking updated');
        } catch (err) {
            console.error(err);
            toast.error('Failed to update linking');
        } finally {
            setSaving(false);
        }
    };

    const loadSecrets = async () => {
        try {
            const res = await listSecrets();
            setSecrets(res);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (activeTab === 'secrets') {
            loadSecrets();
        }
    }, [activeTab]);

    const handleCreateSecret = async (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const res = await createSecret({
                name: secretForm.name,
                valid_from: secretForm.valid_from ? new Date(secretForm.valid_from).toISOString() : undefined,
                valid_to: secretForm.valid_to ? new Date(secretForm.valid_to).toISOString() : undefined,
            });
            setNewSecretResponse(res);
            setShowCreateSecret(false);
            setSecretForm({
                name: '',
                valid_from: formatLocalDate(new Date()),
                valid_to: formatLocalDate(new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())),
            });
            loadSecrets();
            toast.success('Secret created successfully');
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSecret = async (keyId: string) => {
        setActionLoading(true);
        try {
            await deleteSecret(keyId);
            setShowDeleteSecretConfirmKeyId(null);
            setDeleteSecretConfirmText('');
            loadSecrets();
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        setActionLoading(true);
        try {
            await deleteSpn();
            toast.success('Service principal deleted');
            navigate('/m/service-principals');
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete service principal');
            setActionLoading(false);
        }
    };

    const handleCopy = () => {
        if (!newSecretResponse) return;
        navigator.clipboard.writeText(newSecretResponse.client_secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading || !servicePrincipal) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader />
            </div>
        );
    }

    const tabItems = [
        { id: 'details', label: 'Details', icon: Settings },
        { id: 'secrets', label: 'Secrets', icon: Key },
        { id: 'links', label: 'Links', icon: LinkIcon },
    ];

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500 pb-20">
            <PageHeader
                title={
                    <span>
                        {servicePrincipal.name}{" "}
                        <button
                            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors p-1"
                            onClick={() => {
                                navigator.clipboard.writeText(servicePrincipal.client_id);
                                toast.success("Client ID copied");
                            }}
                        >
                            <Copy className="w-3.5 h-3.5" />
                        </button>
                    </span>
                }
                breadcrumbs={
                    <>
                        <button
                            onClick={() => navigate('/m/service-principals')}
                            className="hover:text-(--primary) transition-colors"
                        >
                            Service Principals
                        </button>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <span className="text-slate-900 dark:text-gray-100 font-medium">
                            {servicePrincipal.name}
                        </span>
                    </>
                }
                subtitle={
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-slate-400">
                            Client ID:{" "}
                            <span className="text-slate-500 font-mono tracking-tight">
                                {servicePrincipal.client_id}
                            </span>
                        </span>
                    </div>
                }
                actions={<></>}
            />

            <TabBar
                tabs={tabItems}
                activeTab={activeTab}
                onChange={setActiveTab}
                className="mb-8"
            />

            <div className="mt-8">
                {activeTab === 'details' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white dark:bg-slate-950 rounded-2xl p-6 lg:p-8 border border-slate-100 dark:border-slate-900">
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">General Settings</h2>
                                <form onSubmit={handleUpdateDetails} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            className="w-full h-24 p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all resize-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Redirect URI (Optional)
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.redirect_uri}
                                            onChange={(e) => setFormData({ ...formData, redirect_uri: e.target.value })}
                                            className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
                                            placeholder="e.g., https://app.example.com/callback"
                                        />
                                    </div>

                                    <div className="pt-4 flex justify-end">
                                        <Button
                                            type="submit"
                                            isLoading={saving}
                                            className="bg-[#1e293b] hover:bg-slate-800 text-white rounded-xl px-6 h-11"
                                        >
                                            Save Changes
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="border border-red-200/60 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 rounded-2xl overflow-hidden shadow-sm">
                                <div className="px-6 py-4 flex items-center gap-2 border-b border-red-100/60 dark:border-red-900/30">
                                    <AlertTriangle className="w-4.5 h-4.5 text-red-500" />
                                    <h3 className="font-bold text-red-600 dark:text-red-400 text-[15px]">
                                        Danger Zone
                                    </h3>
                                </div>
                                <div className="px-6 py-5 flex flex-col gap-4">
                                    <div>
                                        <h4 className="font-bold text-slate-900 dark:text-red-100 text-[13px]">
                                            Delete this service principal
                                        </h4>
                                        <p className="text-xs text-slate-600 dark:text-red-300 mt-1 mb-4">
                                            Deleting this service principal will immediately revoke its access.
                                            This action cannot be undone.
                                        </p>
                                    </div>
                                    {!showDeleteConfirm ? (
                                        <Button
                                            variant="danger"
                                            className="w-full font-bold px-5 bg-red-600 hover:bg-red-700 rounded-xl text-[13px]"
                                            onClick={() => setShowDeleteConfirm(true)}
                                        >
                                            Delete Service Principal
                                        </Button>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                                Type <span className="font-bold">"{servicePrincipal.name}"</span> to confirm
                                            </p>
                                            <div className="flex flex-col gap-2">
                                                <input
                                                    type="text"
                                                    value={deleteConfirmText}
                                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                                    className="w-full px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                                                    placeholder={servicePrincipal.name}
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="danger"
                                                        className="flex-1 font-bold bg-red-600 hover:bg-red-700 rounded-md text-xs h-8 border-0"
                                                        disabled={deleteConfirmText !== servicePrincipal.name}
                                                        isLoading={actionLoading}
                                                        onClick={handleDelete}
                                                    >
                                                        Confirm
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        className="flex-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-xs h-8 bg-transparent"
                                                        onClick={() => {
                                                            setShowDeleteConfirm(false);
                                                            setDeleteConfirmText('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'secrets' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-slate-950 rounded-2xl p-6 lg:p-8 border border-slate-100 dark:border-slate-900">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                        <Key className="w-5 h-5 text-orange-500" />
                                        Client Secrets
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        Manage client secrets for this Service Principal. Multiple secrets can co-exist.
                                    </p>
                                </div>
                                {!showCreateSecret && !newSecretResponse && (
                                    <Button
                                        onClick={() => setShowCreateSecret(true)}
                                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 h-10 flex items-center gap-1.5 font-semibold shrink-0"
                                    >
                                        <Plus className="w-4 h-4" />
                                        New Secret
                                    </Button>
                                )}
                            </div>

                            {newSecretResponse && (
                                <div className="bg-emerald-50 dark:bg-emerald-955/30 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800/40 mb-8 animate-in fade-in slide-in-from-top-4">
                                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 mb-4 text-sm font-bold">
                                        <Check className="w-5 h-5" />
                                        Secret Created Successfully
                                    </div>
                                    <div className="mb-6 flex items-start gap-2.5 text-emerald-600 dark:text-emerald-500 bg-white/60 dark:bg-black/20 p-4 rounded-xl text-xs font-medium">
                                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                        Please copy your new client secret now. You won't be able to see it again!
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600/70 dark:text-emerald-400/70 mb-1.5">Secret Name</div>
                                            <div className="font-mono text-sm bg-white dark:bg-slate-900 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800/50 text-slate-700 dark:text-slate-300">
                                                {newSecretResponse.name}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600/70 dark:text-emerald-400/70 mb-1.5">Client Secret (Plaintext)</div>
                                            <div className="flex items-center gap-2">
                                                <div className="font-mono text-sm bg-white dark:bg-slate-900 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800/50 text-slate-700 dark:text-slate-300 flex-1 break-all select-all">
                                                    {newSecretResponse.client_secret}
                                                </div>
                                                <Button
                                                    type="button"
                                                    onClick={handleCopy}
                                                    variant="outline"
                                                    className="h-11 px-4 border-emerald-200 dark:border-emerald-800/50 hover:bg-white dark:hover:bg-slate-800"
                                                >
                                                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => setNewSecretResponse(null)}
                                        className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 font-semibold"
                                    >
                                        I've copied the secret
                                    </Button>
                                </div>
                            )}

                            {showCreateSecret && (
                                <div className="bg-slate-50 dark:bg-slate-800/30 border border-slate-200/60 dark:border-slate-800/60 rounded-2xl p-6 mb-8 animate-in fade-in slide-in-from-top-4">
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Generate New Client Secret</h4>
                                    <form onSubmit={handleCreateSecret} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">Description / Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="e.g. Production API Key"
                                                    value={secretForm.name}
                                                    onChange={(e) => setSecretForm({ ...secretForm, name: e.target.value })}
                                                    className="w-full h-10 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">Valid From</label>
                                                <input
                                                    type="datetime-local"
                                                    required
                                                    value={secretForm.valid_from}
                                                    onChange={(e) => setSecretForm({ ...secretForm, valid_from: e.target.value })}
                                                    className="w-full h-10 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">Valid Till / Expiration</label>
                                                <input
                                                    type="datetime-local"
                                                    required
                                                    value={secretForm.valid_to}
                                                    onChange={(e) => setSecretForm({ ...secretForm, valid_to: e.target.value })}
                                                    className="w-full h-10 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-3 pt-2">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                className="h-10 px-4 text-slate-500 hover:text-slate-700 bg-transparent rounded-lg"
                                                onClick={() => setShowCreateSecret(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                isLoading={actionLoading}
                                                className="h-10 px-5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold"
                                            >
                                                Generate
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {secrets.length === 0 ? (
                                <div className="text-center py-12 bg-slate-50/50 dark:bg-slate-800/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                                    <Key className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No client secrets found</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 mb-4">Create a secret to start authenticating as this service principal.</p>
                                    {!showCreateSecret && (
                                        <Button
                                            onClick={() => setShowCreateSecret(true)}
                                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 h-9 text-xs font-semibold"
                                        >
                                            <Plus className="w-3.5 h-3.5 mr-1" />
                                            Create First Secret
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse text-left">
                                        <thead>
                                            <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider bg-slate-50/30 dark:bg-slate-800/10">
                                                <th className="px-4 py-3">Description Name</th>
                                                <th className="px-4 py-3">Secret Masked</th>
                                                <th className="px-4 py-3">Valid From</th>
                                                <th className="px-4 py-3">Valid Till</th>
                                                <th className="px-4 py-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                                            {secrets.map((secret) => {
                                                const isExpired = secret.valid_to ? new Date(secret.valid_to) < new Date() : false;
                                                return (
                                                    <tr key={secret.key_id} className="group hover:bg-slate-50/30 dark:hover:bg-slate-800/20 transition-colors">
                                                        <td className="px-4 py-4 font-bold text-slate-900 dark:text-white">
                                                            {secret.name}
                                                        </td>
                                                        <td className="px-4 py-4 font-mono text-xs text-slate-500 dark:text-slate-400 select-all">
                                                            {secret.masked_key}
                                                        </td>
                                                        <td className="px-4 py-4 text-xs text-slate-500 dark:text-slate-400">
                                                            {secret.valid_from ? new Date(secret.valid_from).toLocaleString() : 'Immediate'}
                                                        </td>
                                                        <td className="px-4 py-4 text-xs">
                                                            <span className={isExpired ? "text-red-500 font-medium" : "text-slate-500 dark:text-slate-400"}>
                                                        {secret.valid_to ? new Date(secret.valid_to).toLocaleString() : 'Never'}
                                                            </span>
                                                            {isExpired && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400 rounded">Expired</span>}
                                                        </td>
                                                        <td className="px-4 py-4 text-right">
                                                            {showDeleteSecretConfirmKeyId === secret.key_id ? (
                                                                <div className="flex flex-col items-end gap-1.5 animate-in fade-in duration-205">
                                                                    <span className="text-[11px] text-red-500 font-medium">
                                                                        Type <span className="font-bold">"{secret.name}"</span> to confirm:
                                                                    </span>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <input
                                                                            type="text"
                                                                            value={deleteSecretConfirmText}
                                                                            onChange={(e) => setDeleteSecretConfirmText(e.target.value)}
                                                                            className="px-2 py-1 text-xs border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-red-500 w-36 h-7"
                                                                            placeholder={secret.name}
                                                                        />
                                                                        <Button
                                                                            variant="danger"
                                                                            className="font-bold bg-red-600 hover:bg-red-700 rounded-md text-[10px] h-7 px-2.5 border-0 flex items-center justify-center"
                                                                            disabled={deleteSecretConfirmText !== secret.name}
                                                                            isLoading={actionLoading}
                                                                            onClick={() => handleDeleteSecret(secret.key_id)}
                                                                        >
                                                                            Confirm
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-[10px] h-7 px-2 bg-transparent border border-slate-200 dark:border-slate-700 rounded-md flex items-center justify-center"
                                                                            onClick={() => {
                                                                                setShowDeleteSecretConfirmKeyId(null);
                                                                                setDeleteSecretConfirmText('');
                                                                            }}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => {
                                                                        setShowDeleteSecretConfirmKeyId(secret.key_id);
                                                                        setDeleteSecretConfirmText('');
                                                                    }}
                                                                    className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 rounded-lg hover:bg-red-50/50 dark:hover:bg-red-950/20 transition-colors"
                                                                    title="Delete secret"
                                                                >
                                                                    <Trash2 className="w-4.5 h-4.5" />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'links' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white dark:bg-slate-950 rounded-2xl p-6 border border-slate-100 dark:border-slate-900">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">Entity Linking</h3>
                            <form onSubmit={handleUpdateLinks} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-4">
                                        Link to what type of entity?
                                    </label>
                                    <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="linkType" 
                                                checked={linkData.type === 'none'} 
                                                onChange={() => setLinkData({ type: 'none', id: '' })}
                                                className="w-4 h-4 text-(--primary) border-slate-300 focus:ring-(--primary)"
                                            />
                                            <span className="text-sm text-slate-700 dark:text-slate-300">None (Standalone)</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="linkType" 
                                                checked={linkData.type === 'application'} 
                                                onChange={() => setLinkData({ type: 'application', id: '' })}
                                                className="w-4 h-4 text-(--primary) border-slate-300 focus:ring-(--primary)"
                                            />
                                            <AppWindow className="w-4 h-4 text-slate-400" />
                                            <span className="text-sm text-slate-700 dark:text-slate-300">Application</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="linkType" 
                                                checked={linkData.type === 'user'} 
                                                onChange={() => setLinkData({ type: 'user', id: '' })}
                                                className="w-4 h-4 text-(--primary) border-slate-300 focus:ring-(--primary)"
                                            />
                                            <User className="w-4 h-4 text-slate-400" />
                                            <span className="text-sm text-slate-700 dark:text-slate-300">User</span>
                                        </label>
                                    </div>
                                </div>

                                {linkData.type !== 'none' && (
                                    <div className="animate-in fade-in slide-in-from-top-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            {linkData.type === 'application' ? 'Application' : 'User'}
                                        </label>
                                        <select
                                            required
                                            value={linkData.id}
                                            onChange={(e) => setLinkData({ ...linkData, id: e.target.value })}
                                            className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all appearance-none"
                                        >
                                            <option value="" disabled>Select {linkData.type}</option>
                                            {linkData.type === 'application' && applications.map((app) => (
                                                <option key={app.id} value={app.id}>
                                                    {app.name}
                                                </option>
                                            ))}
                                            {linkData.type === 'user' && users.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name} ({user.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <Button
                                        type="submit"
                                        isLoading={saving}
                                        className="bg-[#1e293b] hover:bg-slate-800 text-white rounded-xl px-6 h-11 w-full"
                                    >
                                        Save Linking Configuration
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
